import type { DashboardDto, TransactionDto } from "@home/shared";
import { useState } from "react";
import { api } from "../api.ts";
import type { TxType } from "../components/AddTxSheet.tsx";
import { EmptyState, SkeletonCard } from "../components/EmptyState.tsx";
import { GoalsCard } from "../components/Goals.tsx";
import { PaySheet, RecurringSheet } from "../components/RecurringSheet.tsx";
import { SetupCard } from "../components/Setup.tsx";
import { TxDetailSheet } from "../components/TxDetailSheet.tsx";
import { TxList } from "../components/TxList.tsx";
import { changePct, currentMonth, dayTitle, money, monthName } from "../format.ts";
import { t, type Lang } from "../i18n.ts";
import { useRemote } from "../useRemote.ts";

/** Сколько последних записей показывать на главной; остальные — во вкладке «История». */
const RECENT = 5;
const UPCOMING = 3;

interface Props {
  lang: Lang;
  onAdd: (type: TxType) => void;
  onOpenOps: () => void;
  onOpenReport: () => void;
  onOpenDebts: () => void;
}

/** Главная: сколько потратили в этом месяце, одна кнопка «Записать», последние записи, ближайшие платежи, копилки. */
export function HomePage({ lang, onAdd, onOpenOps, onOpenReport, onOpenDebts }: Props) {
  const month = currentMonth();
  // Тот же ключ, что у отчёта за текущий месяц: общий кэш, обновление по SSE.
  const remote = useRemote(`dashboard:${month}`, () => api.dashboard(month));
  const [picked, setPicked] = useState<TransactionDto | null>(null);
  const [payments, setPayments] = useState(false);
  const [paying, setPaying] = useState<DashboardDto["upcoming"][number] | null>(null);
  const d = remote.data;

  return (
    <main className="dash">
      {remote.staleSince && (
        <div className="banner banner-warn">
          {t("offlineData", { time: new Date(remote.staleSince).toLocaleString(lang === "uz" ? "uz-UZ" : "ru-RU", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" }) })}
        </div>
      )}
      {!d && remote.loading && (
        <>
          <SkeletonCard lines={2} />
          <SkeletonCard lines={4} />
        </>
      )}
      {!d && !remote.loading && remote.error != null && <p className="empty">{t("loadError")}</p>}

      {d && (
        <>
          <SpentCard d={d} lang={lang} onReport={onOpenReport} />
          <button type="button" className="btn btn-primary btn-big" onClick={() => onAdd("expense")}>
            ＋ {t("addOp")}
          </button>
          <SetupCard lang={lang} hasDebts={d.debts !== null} onDebts={onOpenDebts} />

          <section className="dash-card">
            <div className="dash-card-head">
              <h3>{t("recentTitle")}</h3>
              {d.recent.length > 0 && (
                <button type="button" className="btn-small-ghost" onClick={onOpenOps}>
                  {t("all")} ›
                </button>
              )}
            </div>
            {d.recent.length === 0 ? (
              <EmptyState icon="🧾" title={t("noOpsYet")} text={t("noOpsYetHint")} />
            ) : (
              <TxList items={d.recent.slice(0, RECENT)} lang={lang} onPick={setPicked} />
            )}
          </section>

          <Upcoming d={d} lang={lang} onManage={() => setPayments(true)} onPay={setPaying} onDebts={onOpenDebts} />
          {d.goals.length > 0 && <GoalsCard goals={d.goals} lang={lang} />}
        </>
      )}

      {picked && <TxDetailSheet tx={picked} lang={lang} onClose={() => setPicked(null)} />}
      {payments && <RecurringSheet lang={lang} onClose={() => setPayments(false)} />}
      {paying && <PaySheet payment={paying} lang={lang} onClose={() => setPaying(null)} />}
    </main>
  );
}

/** Одна главная цифра — расходы месяца; рядом доход и остаток на счетах, ссылка на отчёт. */
function SpentCard({ d, lang, onReport }: { d: DashboardDto; lang: Lang; onReport: () => void }) {
  const { totals } = d;
  const pct = changePct(totals.expense, totals.prevExpense);
  return (
    <section className="dash-card home-hero">
      <div className="hero-label">{t("spentIn", { month: monthName(d.month, lang) })}</div>
      <div className="hero-value">{money(totals.expense, "UZS", lang)}</div>
      {pct !== null && pct !== 0 && (
        <div className={pct > 0 ? "delta bad" : "delta good"}>{pct > 0 ? `↑ ${t("morePct", { p: pct })}` : `↓ ${t("lessPct", { p: -pct })}`}</div>
      )}
      <div className="home-stats">
        <div>
          <div className="stat-label">{t("incomeL")}</div>
          <b className={totals.income > 0 ? "income" : ""}>{money(totals.income, "UZS", lang, totals.income > 0)}</b>
        </div>
        <div>
          <div className="stat-label">{t("balanceTotal")}</div>
          <b className={d.totalBalanceUzs < 0 ? "negative" : ""}>{money(d.totalBalanceUzs, "UZS", lang)}</b>
        </div>
      </div>
      {d.fxMissing && <div className="hint">{t("fxMissing")}</div>}
      {totals.income > 0 && totals.net < 0 && <div className="home-warn">⚠️ {t("overspentBy", { amount: money(-totals.net, "UZS", lang) })}</div>}
      <button type="button" className="hero-link" onClick={onReport}>
        <span>📊 {t("categoriesTitle")}</span>
        <span className="chevron" aria-hidden="true">
          ›
        </span>
      </button>
    </section>
  );
}

/** Ближайшие регулярные платежи и платёж по кредиту — одним списком по дате: «что скоро платить». */
function Upcoming({
  d,
  lang,
  onManage,
  onPay,
  onDebts,
}: {
  d: DashboardDto;
  lang: Lang;
  onManage: () => void;
  onPay: (payment: DashboardDto["upcoming"][number]) => void;
  onDebts: () => void;
}) {
  const debt = d.debts?.nextPayment ?? null;
  const rows = d.upcoming.map((u) => ({ key: u.id, name: u.name, dueDate: u.dueDate, amount: u.amount, currency: u.currency, debt: false, overdue: false }));
  // Рассрочка часто заведена и платежом, и долгом — не показываем её дважды.
  if (debt && !rows.some((r) => r.name === debt.debtName)) {
    rows.push({ key: `debt:${debt.debtName}`, name: debt.debtName, dueDate: debt.dueDate, amount: debt.amount, currency: debt.currency, debt: true, overdue: debt.overdue });
  }
  rows.sort((a, b) => a.dueDate.localeCompare(b.dueDate));
  if (rows.length === 0) return null;
  return (
    <section className="dash-card">
      <div className="dash-card-head">
        <h3>🔔 {t("upcoming")}</h3>
        <button type="button" className="btn-small-ghost" onClick={onManage}>
          {t("manage")} ›
        </button>
      </div>
      <ul className="plain-list">
        {rows.slice(0, UPCOMING).map((r) => {
          const body = (
            <>
              <span>
                {r.debt && "🏦 "}
                {r.name}{" "}
                {r.overdue ? <span className="negative">· ⚠️ {t("overdueL")}</span> : <span className="muted">· {dayTitle(r.dueDate, lang)}</span>}
              </span>
              <b>{money(r.amount, r.currency, lang)}</b>
            </>
          );
          // Платёж по кредиту ведёт в «Кредиты и рассрочки», регулярный — к «Оплачено / Пропустить».
          const payment = r.debt ? null : d.upcoming.find((u) => u.id === r.key)!;
          return (
            <li key={r.key} className="li-button">
              <button type="button" className="upcoming-row" onClick={() => (payment ? onPay(payment) : onDebts())}>
                {body}
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
